import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiScopes, getMsalInstance } from '../auth/msalConfig';
import { getNotificationZIndex } from './zIndexLayers';

export class TokenExpirationHandler {
  private static isRedirecting = false;

  /**
   * Handles token expiration by clearing session and redirecting to login
   */
  static async handleTokenExpiration(error: any): Promise<void> {
    // Prevent multiple redirects
    if (this.isRedirecting) {
      return;
    }

    // Check if this is a token expiration error
    if (this.isTokenExpirationError(error)) {
      console.log('[TokenExpiration] Token expired, redirecting to login...');
      this.isRedirecting = true;

      try {
        // Show a brief notification to the user
        this.showExpirationNotification();

        // Clear any cached tokens and session data
        await this.clearAuthenticationState();

        // Add a small delay to let the user see the notification
        setTimeout(() => {
          // Suppress browser's "Leave site?" dialog — unsaved form changes are
          // already lost once the session expires, so the warning adds no value.
          window.onbeforeunload = null;
          // Redirect to home page (login page) with session expired parameter
          window.location.href = '/?sessionExpired=true';
        }, 2000);
      } catch (clearError) {
        console.error('[TokenExpiration] Error clearing auth state:', clearError);
        // Force redirect even if clearing fails
        setTimeout(() => {
          window.onbeforeunload = null;
          window.location.href = '/?sessionExpired=true';
        }, 1000);
      }
    }
  }

  /**
   * Shows a notification to the user about session expiration
   */
  private static showExpirationNotification(): void {
    // Create a simple notification element
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #fff3cd;
      border: 1px solid #ffeaa7;
      border-radius: 8px;
      padding: 16px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: ${getNotificationZIndex()};
      max-width: 400px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;

    notification.innerHTML = `
      <div style="display: flex; align-items: center; gap: 12px;">
        <div style="color: #856404; font-size: 18px;">⚠️</div>
        <div>
          <div style="font-weight: 600; color: #856404; margin-bottom: 4px;">Session Expired</div>
          <div style="color: #856404; font-size: 14px;">You will be redirected to the login page.</div>
        </div>
      </div>
    `;

    document.body.appendChild(notification);

    // Remove notification after redirect
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 3000);
  }

  /**
   * Checks if the error is related to token expiration
   * IMPORTANT: Only genuine MSAL token errors should trigger session expiration.
   * All backend HTTP errors should be logged but NOT cause session expiration in development.
   */
  static isTokenExpirationError(error: any): boolean {
    if (!error) return false;

    // Check for specific error messages that should NOT be treated as token expiration
    const errorMessage = error.message?.toLowerCase() || '';
    const errorCode = error.errorCode?.toLowerCase() || '';
    const errorStatus = error.status || error.statusCode || 0;

    // IMPORTANT: Exclude "no_account_error" - this is for new users, not token expiration
    if (errorMessage.includes('no_account_error') ||
      errorMessage.includes('no account object provided') ||
      errorCode.includes('no_account_error')) {
      return false;
    }

    // NEVER treat ANY HTTP status code errors as token expiration
    // These are backend errors that should be debugged, not cause re-authentication
    if (errorStatus > 0) {
      console.warn(`[TokenExpiration] Backend HTTP error ${errorStatus} detected - NOT treating as token expiration:`, error);
      return false;
    }

    // Exclude any error that mentions HTTP status codes in the message
    if (errorMessage.includes('http error') ||
      errorMessage.includes('status:') ||
      /\b[4-5]\d\d\b/.test(errorMessage)) { // Regex for 4xx or 5xx status codes
      console.warn('[TokenExpiration] HTTP error in message - NOT treating as token expiration:', error);
      return false;
    }

    // Exclude errors that specifically indicate backend/data issues
    if (errorMessage.includes('person not found') ||
      errorMessage.includes('user not found') ||
      errorMessage.includes('does not exist') ||
      errorMessage.includes('bad request') ||
      errorMessage.includes('internal server error') ||
      errorMessage.includes('service unavailable') ||
      errorMessage.includes('not found') ||
      errorMessage.includes('unauthorized') ||
      errorMessage.includes('forbidden')) {
      return false;
    }

    // Check for iframe timeout errors which indicate the silent token refresh failed
    // (session expired, network issues, or Azure AD slowness) — all require re-login
    if (errorMessage.includes('monitor_window_timeout') ||
      errorMessage.includes('token acquisition in iframe failed due to timeout') ||
      errorCode.includes('timed_out') ||
      errorMessage.includes('timed_out')) {
      console.log('[TokenExpiration] iframe/silent token timeout detected - treating as token expiration');
      return true;
    }

    // Check for InteractionRequiredAuthError (most reliable MSAL indicator).
    // Note: InteractionRequiredAuthError extends BrowserAuthError, so checking instanceof
    // BrowserAuthError would be too broad — it also catches popup_blocked, popup_window_error,
    // interaction_in_progress, etc. which are NOT token expiration.
    if (error instanceof InteractionRequiredAuthError) {
      console.log('[TokenExpiration] InteractionRequiredAuthError detected - genuine token expiration');
      return true;
    }

    // Only treat errors as token expiration if they are specifically MSAL token-related
    // and do NOT have HTTP status codes
    const isMsalTokenError = (
      (errorMessage.includes('refresh_token_expired') ||
        errorMessage.includes('token_expired') ||
        errorMessage.includes('interaction_required') ||
        errorMessage.includes('login_required') ||
        errorCode.includes('token_expired') ||
        errorCode.includes('interaction_required') ||
        errorCode.includes('login_required')) &&
      !errorStatus // Ensure no HTTP status code is present
    );

    if (isMsalTokenError) {
      console.log('[TokenExpiration] Genuine MSAL token error detected:', error);
    }

    return isMsalTokenError;
  }

  /**
   * Clears all authentication state
   */
  private static async clearAuthenticationState(): Promise<void> {
    try {
      console.log('[TokenExpiration] Starting comprehensive authentication state cleanup...');

      const msalInstance = await getMsalInstance();

      // Clear MSAL cache by clearing the cache storage
      // Note: We can't remove individual accounts, so we clear the entire cache
      if (msalInstance.clearCache) {
        await msalInstance.clearCache();
      }

      // Clear session storage auth keys
      sessionStorage.removeItem('isAdmin');
      sessionStorage.removeItem('isManager');

      // Clear local storage auth-related items
      const authKeys = Object.keys(localStorage).filter(key =>
        key.includes('msal') ||
        key.includes('auth') ||
        key.includes('token')
      );

      authKeys.forEach(key => {
        console.log(`[TokenExpiration] Clearing localStorage key: ${key}`);
        localStorage.removeItem(key);
      });

      // Clear service caches
      try {
        // Clear project roles cache
        const { ProjectRolesService } = await import('../features/CVEditor/features/ProjectExperience/services/projectRolesService');
        ProjectRolesService.clearCache();
        console.log('[TokenExpiration] ProjectRolesService cache cleared');
      } catch (error) {
        console.warn('[TokenExpiration] Could not clear ProjectRolesService cache:', error);
      }

      try {
        // Clear technology category cache
        const { TechnologyCategoryService } = await import('../services/technologyCategoryService');
        const techCategoryService = new TechnologyCategoryService();
        techCategoryService.clearAllCache();
        console.log('[TokenExpiration] TechnologyCategoryService cache cleared');
      } catch (error) {
        console.warn('[TokenExpiration] Could not clear TechnologyCategoryService cache:', error);
      }

      // Clear administration service cache
      try {
        const { AdministrationService } = await import('../services/administrationService');
        AdministrationService.clearCache();
        console.log('[TokenExpiration] AdministrationService cache cleared');
      } catch (error) {
        console.warn('[TokenExpiration] Could not clear AdministrationService cache:', error);
      }

      console.log('[TokenExpiration] Authentication state cleared comprehensively');
    } catch (error) {
      console.error('[TokenExpiration] Error clearing authentication state:', error);
    }
  }

  /**
   * Attempts to refresh token silently, returns true if successful
   */
  static async attemptSilentTokenRefresh(): Promise<boolean> {
    try {
      const msalInstance = await getMsalInstance();
      const accounts = msalInstance.getAllAccounts();

      if (accounts.length === 0) {
        return false;
      }

      // Try to acquire token silently
      await msalInstance.acquireTokenSilent({
        scopes: apiScopes.backendApi.length > 0 ? apiScopes.backendApi : ['User.Read'],
        account: accounts[0]
      });

      return true;
    } catch (error) {
      console.log('[TokenExpiration] Silent token refresh failed:', error);

      // Check if this is a timeout error and handle appropriately
      const errorMessage = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
      if (errorMessage.includes('monitor_window_timeout') ||
        errorMessage.includes('token acquisition in iframe failed due to timeout')) {
        console.log('[TokenExpiration] Silent refresh timeout detected - will attempt interactive refresh');

        try {
          // Only attempt interactive login if we're not already redirecting
          if (!this.isRedirecting) {
            await this.handleTokenExpiration(error);
          }
        } catch (interactiveError) {
          console.error('[TokenExpiration] Interactive refresh also failed:', interactiveError);
        }
      }

      return false;
    }
  }

  /**
   * Proactively checks if tokens need refreshing and attempts to refresh them
   * This can be called periodically to ensure tokens are always fresh
   */
  static async checkAndRefreshTokens(): Promise<boolean> {
    try {
      const msalInstance = await getMsalInstance();
      const accounts = msalInstance.getAllAccounts();

      if (accounts.length === 0) {
        console.log('[TokenExpiration] No accounts found for token refresh');
        return false;
      }

      // Try to acquire token silently - this will refresh if needed
      const response = await msalInstance.acquireTokenSilent({
        scopes: apiScopes.backendApi,
        account: accounts[0]
      });

      if (response.accessToken) {
        console.log('[TokenExpiration] Token refresh successful');
        return true;
      }

      return false;
    } catch (error) {
      // If this fails, it's likely a genuine authentication issue
      if (this.isTokenExpirationError(error)) {
        console.warn('[TokenExpiration] Token refresh failed - genuine expiration detected');
        await this.handleTokenExpiration(error);
        return false;
      }

      console.warn('[TokenExpiration] Token refresh failed with non-auth error:', error);
      return false;
    }
  }

  /**
   * Sets up periodic token refresh to prevent expiration issues
   * Call this once during app initialization
   */
  static setupPeriodicTokenRefresh(): () => void {
    // Initialize the session activity tracking
    let lastActivityTime = Date.now();
    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click', 'input'];

    // Update last activity time when user interacts with the page
    const updateActivityTime = () => {
      lastActivityTime = Date.now();

      // Remove the "inactive" class if present
      document.body.classList.remove('user-inactive');
    };

    // Add event listeners for user activity
    activityEvents.forEach(event => {
      document.addEventListener(event, updateActivityTime, { passive: true });
    });

    // Check tokens every 5 minutes
    const tokenIntervalId = setInterval(async () => {
      await this.checkAndRefreshTokens();
    }, 5 * 60 * 1000); // 5 minutes

    // Check user activity every 1 minute
    // If idle for more than 30 minutes, reduce token refresh frequency
    // If active again, restore normal token refresh frequency
    const activityIntervalId = setInterval(() => {
      const currentTime = Date.now();
      const inactiveTime = currentTime - lastActivityTime;

      // If user has been inactive for more than 30 minutes
      if (inactiveTime > 30 * 60 * 1000) {
        // Add an indicator class to the body
        document.body.classList.add('user-inactive');

        // If inactive but the window is visible, attempt a token refresh to keep the session alive
        if (document.visibilityState === 'visible') {
          console.log('[TokenExpiration] User inactive but page visible, refreshing token to keep session alive');
          this.checkAndRefreshTokens();
        }
      }
      // If user was previously marked inactive but is now active again
      else if (document.body.classList.contains('user-inactive')) {
        document.body.classList.remove('user-inactive');

        // Force a token refresh when returning from inactivity
        console.log('[TokenExpiration] User returned from inactivity, refreshing tokens');
        this.checkAndRefreshTokens();
      }
    }, 60 * 1000); // 1 minute

    // Return cleanup function that removes all event listeners and clears intervals
    return () => {
      clearInterval(tokenIntervalId);
      clearInterval(activityIntervalId);
      activityEvents.forEach(event => {
        document.removeEventListener(event, updateActivityTime);
      });
    };
  }
} 